import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Users,
  Mail,
  Search,
  Filter,
  Plus,
  Eye,
  Trash2,
  Send,
  CheckCircle,
  XCircle,
  Calendar,
  TrendingUp,
  Loader2,
  MailOpen,
  MousePointerClick,
  Building2,
  UserCheck,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const Prospects = () => {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    interested: 0,
    converted: 0
  });
  const [prospectQuotes, setProspectQuotes] = useState({}); // Map of prospect_id -> quote count
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Email dialog
  const [emailDialog, setEmailDialog] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(true);

  useEffect(() => {
    fetchProspects();
    fetchQuoteCounts();
  }, [statusFilter, searchQuery]);

  const fetchProspects = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`/prospects?${params.toString()}`);
      setProspects(response.data.prospects || []);
      
      // Calculate stats
      const all = response.data.prospects || [];
      setStats({
        total: all.length,
        new: all.filter(p => p.status === 'new').length,
        contacted: all.filter(p => p.status === 'contacted').length,
        interested: all.filter(p => p.status === 'interested').length,
        converted: all.filter(p => p.status === 'converted').length
      });
    } catch (error) {
      console.error('Failed to fetch prospects:', error);
      toast.error('Failed to load prospects');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuoteCounts = async () => {
    try {
      const response = await api.get('/quotes');
      const quotes = response.data.quotes || [];
      
      // Count quotes per prospect
      const counts = {};
      quotes.forEach(quote => {
        counts[quote.prospect_id] = (counts[quote.prospect_id] || 0) + 1;
      });
      
      setProspectQuotes(counts);
    } catch (error) {
      console.error('Failed to fetch quote counts:', error);
    }
  };

  const navigateToClientQuotes = (prospect) => {
    // If converted, navigate to client's quotes tab
    if (prospect.status === 'converted' && prospect.converted_to_client_id) {
      navigate(`/clients/${prospect.converted_to_client_id}`, { state: { activeTab: 'quotes' } });
    } else {
      // For non-converted prospects, we could create a quotes view page
      // For now, just show a message
      toast.info('Generate a quote first or convert to client to view quotes');
    }
  };

  const handleGenerateEmail = async (prospect) => {
    setSelectedProspect(prospect);
    setGeneratingEmail(true);
    setEmailDialog(true);
    
    try {
      const response = await api.post(`/prospects/${prospect.id}/generate-email`);
      setEmailSubject(response.data.subject);
      setEmailBody(response.data.body);
      toast.success('Email generated successfully!');
    } catch (error) {
      console.error('Failed to generate email:', error);
      toast.error('Failed to generate email');
      setEmailDialog(false);
    } finally {
      setGeneratingEmail(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedProspect) return;
    
    setSendingEmail(true);
    try {
      await api.post(`/prospects/${selectedProspect.id}/send-email`, {
        subject: emailSubject,
        body: emailBody
      });
      
      toast.success('Email sent successfully!');
      setEmailDialog(false);
      fetchProspects(); // Refresh list
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleConvert = async (prospectId) => {
    if (!window.confirm('Convert this prospect to a client?')) return;
    
    try {
      const response = await api.post(`/prospects/${prospectId}/convert`);
      toast.success('Prospect converted to client!');
      navigate(`/clients/${response.data.client_id}`);
    } catch (error) {
      console.error('Failed to convert prospect:', error);
      toast.error('Failed to convert prospect');
    }
  };

  const handleDelete = async (prospectId) => {
    if (!window.confirm('Are you sure you want to delete this prospect?')) return;
    
    try {
      await api.delete(`/prospects/${prospectId}`);
      toast.success('Prospect deleted');
      fetchProspects();
    } catch (error) {
      console.error('Failed to delete prospect:', error);
      toast.error('Failed to delete prospect');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'bg-blue-100 text-blue-800', icon: Plus },
      contacted: { color: 'bg-purple-100 text-purple-800', icon: Mail },
      interested: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      not_interested: { color: 'bg-gray-100 text-gray-800', icon: XCircle },
      meeting_scheduled: { color: 'bg-yellow-100 text-yellow-800', icon: Calendar },
      converted: { color: 'bg-emerald-100 text-emerald-800', icon: TrendingUp },
      lost: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.new;
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getEmailStatusBadge = (emailStatus, emailSentAt, emailOpenedAt) => {
    if (emailStatus === 'sent' && emailOpenedAt) {
      return <Badge className="bg-green-100 text-green-800"><MailOpen className="w-3 h-3 mr-1" />Opened</Badge>;
    }
    if (emailStatus === 'sent') {
      return <Badge className="bg-blue-100 text-blue-800"><Send className="w-3 h-3 mr-1" />Sent</Badge>;
    }
    if (emailStatus === 'draft') {
      return <Badge className="bg-gray-100 text-gray-800">Draft</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800">Not Sent</Badge>;
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Prospects</h1>
            <p className="text-gray-600 mt-1">Manage and convert your prospects to clients</p>
          </div>
          <Button
            onClick={() => navigate('/clients')}
            variant="outline"
            className="gap-2"
          >
            <Building2 className="w-4 h-4" />
            View Clients
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">New</p>
                  <p className="text-2xl font-bold">{stats.new}</p>
                </div>
                <Plus className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Contacted</p>
                  <p className="text-2xl font-bold">{stats.contacted}</p>
                </div>
                <Mail className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Interested</p>
                  <p className="text-2xl font-bold">{stats.interested}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Converted</p>
                  <p className="text-2xl font-bold">{stats.converted}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search prospects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="interested">Interested</option>
              <option value="not_interested">Not Interested</option>
              <option value="meeting_scheduled">Meeting Scheduled</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Prospects Detailed List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Prospect List
            <Badge variant="outline" className="ml-2">{prospects.length} total</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : prospects.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No prospects found</p>
              <p className="text-sm mt-1">Create prospects from the Clients page or via Companies House search</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="prospects-table">
                <thead className="bg-gray-50 border-y border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Company Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Director</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Address</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Company No.</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Telephone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {prospects
                    .filter(prospect => statusFilter === 'converted' || prospect.status !== 'converted')
                    .map((prospect) => {
                      // Get director name - could be from directors array or director_name field
                      const directorName = prospect.director_name || 
                        (prospect.directors && prospect.directors.length > 0 
                          ? prospect.directors[0].name 
                          : prospect.contact_name || '—');
                      
                      return (
                        <tr 
                          key={prospect.id} 
                          className="hover:bg-blue-50/50 transition-colors cursor-pointer"
                          data-testid={`prospect-row-${prospect.id}`}
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <div>
                                <p className="font-semibold text-gray-900">{prospect.company_name}</p>
                                {prospect.source === 'client' && (
                                  <Badge variant="outline" className="text-xs mt-1 bg-blue-50 text-blue-700 border-blue-200">
                                    Client
                                  </Badge>
                                )}
                                {prospect.website && (
                                  <a 
                                    href={prospect.website.startsWith('http') ? prospect.website : `https://${prospect.website}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline block mt-0.5"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {prospect.website}
                                  </a>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-sm text-gray-700">{directorName}</p>
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-sm text-gray-700 max-w-xs truncate" title={prospect.address || '—'}>
                              {prospect.address || '—'}
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-sm text-gray-700 font-mono">{prospect.company_number || '—'}</p>
                          </td>
                          <td className="px-4 py-4">
                            {prospect.contact_phone ? (
                              <a 
                                href={`tel:${prospect.contact_phone}`}
                                className="text-sm text-blue-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {prospect.contact_phone}
                              </a>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {prospect.contact_email ? (
                              <a 
                                href={`mailto:${prospect.contact_email}`}
                                className="text-sm text-blue-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {prospect.contact_email}
                              </a>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-col gap-1">
                              {getStatusBadge(prospect.status)}
                              {prospect.email_status && prospect.email_status !== 'draft' && (
                                <div className="mt-1">
                                  {getEmailStatusBadge(prospect.email_status, prospect.email_sent_at, prospect.email_opened_at)}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex justify-end gap-1">
                              {!prospect.generated_email && prospect.source !== 'client' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); handleGenerateEmail(prospect); }}
                                  className="h-8 w-8 p-0"
                                  title="Generate Email"
                                  data-testid={`generate-email-${prospect.id}`}
                                >
                                  <Mail className="w-4 h-4 text-gray-600" />
                                </Button>
                              )}
                              {prospect.generated_email && prospect.email_status !== 'sent' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedProspect(prospect);
                                    setEmailSubject(prospect.email_subject || '');
                                    setEmailBody(prospect.generated_email || '');
                                    setEmailDialog(true);
                                  }}
                                  className="h-8 w-8 p-0"
                                  title="Send Email"
                                  data-testid={`send-email-${prospect.id}`}
                                >
                                  <Send className="w-4 h-4 text-blue-600" />
                                </Button>
                              )}
                              {prospectQuotes[prospect.id] > 0 && prospect.status === 'converted' && prospect.converted_to_client_id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); navigateToClientQuotes(prospect); }}
                                  className="h-8 w-8 p-0 relative"
                                  title="View Quotes"
                                  data-testid={`view-quotes-${prospect.id}`}
                                >
                                  <Eye className="w-4 h-4 text-blue-600" />
                                  <span className="absolute -top-1 -right-1 px-1 py-0.5 text-[10px] bg-blue-500 text-white rounded-full min-w-[16px] text-center">
                                    {prospectQuotes[prospect.id]}
                                  </span>
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); navigate(`/generate-quote/${prospect.id}`); }}
                                className="h-8 w-8 p-0"
                                title="Generate Quote"
                                data-testid={`generate-quote-${prospect.id}`}
                              >
                                <DollarSign className="w-4 h-4 text-emerald-600" />
                              </Button>
                              {prospect.status === 'interested' && prospect.status !== 'converted' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); handleConvert(prospect.id); }}
                                  className="h-8 w-8 p-0"
                                  title="Convert to Client"
                                  data-testid={`convert-${prospect.id}`}
                                >
                                  <UserCheck className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); handleDelete(prospect.id); }}
                                className="h-8 w-8 p-0"
                                title="Delete Prospect"
                                data-testid={`delete-${prospect.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Dialog */}
      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {generatingEmail ? 'Generating Email...' : 'Prospect Email'}
            </DialogTitle>
            <DialogDescription>
              {selectedProspect && `Email for ${selectedProspect.company_name}`}
            </DialogDescription>
          </DialogHeader>
          
          {generatingEmail ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
              <p className="text-gray-600">AI is generating a personalized email...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Subject</label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                />
              </div>
              
              {/* Toggle between HTML Preview and Code View */}
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">Email Body</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowHtmlPreview(true)}
                    className={`px-3 py-1 text-xs rounded ${showHtmlPreview ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowHtmlPreview(false)}
                    className={`px-3 py-1 text-xs rounded ${!showHtmlPreview ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    HTML Code
                  </button>
                </div>
              </div>
              
              <div>
                {showHtmlPreview ? (
                  <div 
                    className="border rounded-lg p-4 bg-white max-h-[400px] overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: emailBody }}
                  />
                ) : (
                  <Textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    rows={15}
                    className="font-mono text-sm"
                  />
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(false)}>
              Cancel
            </Button>
            {!generatingEmail && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleGenerateEmail(selectedProspect)}
                >
                  Regenerate
                </Button>
                <Button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !emailBody}
                  className="gap-2"
                >
                  {sendingEmail ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Send Email</>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Prospects;
